/**
 * Flipped classroom (M5) — PURE types + selectors for the class session plan.
 *
 * A `session_module` links a `class_session` to a `course_module` under a
 * PHASE (before/during/after). This file concentrates the pure, testable logic
 * (grouping by phase, ordering, composing completion ticks by reusing
 * progress that already exists: lesson_progress, assignment_submission,
 * video_watch_progress). Consumed by the UI (Solid) and the write-path (Rust).
 */

/** Flipped-classroom phase (== `session_module.phase`). */
export type PlanPhase = "pre" | "live" | "post";

/** The three phases in pedagogical order (pre → live → post). */
export const PLAN_PHASES: readonly PlanPhase[] = ["pre", "live", "post"] as const;

/** Canonical rank of a phase (pre=0, live=1, post=2). */
export function phaseRank(phase: PlanPhase): number {
  return PLAN_PHASES.indexOf(phase);
}

/** Type guard: is it a valid phase? (the write-path rejects the rest). */
export function isPlanPhase(value: unknown): value is PlanPhase {
  return value === "pre" || value === "live" || value === "post";
}

/**
 * Class session plan item = `session_module` row composed with the `course_module` it
 * references (kind/ref_id/title) so it can be rendered and its tick resolved.
 */
export interface PlanModule {
  /** id of the session_module row. */
  id: string;
  /** id of the referenced course_module. */
  moduleId: string;
  phase: PlanPhase;
  position: number;
  /** kind of the course_module (lesson|resource|video|assignment|forum|live_session…). */
  kind: string | null;
  /** ref_id of the course_module (lesson.id | assignment.id | video_asset.id | …). */
  refId: string | null;
  /** Human-readable module title (for the list). */
  title?: string | null;
  /** Framing note per item (session_module.config.note). */
  note?: string | null;
}

/**
 * Progress sources already existing on the client (local-first), indexed by
 * each kind's natural key. All optional: a kind with no source ⇒ no tick.
 */
export interface PlanCompletionCtx {
  /** lesson_progress.status by lesson id (ref_id of a `lesson` module). */
  lessonStatusByRef?: ReadonlyMap<string, string | null>;
  /** submitted? by assignment id (ref_id of an `assignment` module). */
  submittedByAssignment?: ReadonlyMap<string, boolean>;
  /** video completed? by video_asset id (ref_id of a `video` module). */
  videoDoneByRef?: ReadonlyMap<string, boolean>;
}

/** Completion status of an item: done | pending | null (doesn't score/informational). */
export type ItemCompletion = "done" | "pending" | null;

/**
 * Resolves an item's tick by reusing existing progress. `null` when the kind
 * has no concept of completion (resource, forum, live session): those items
 * are informational and don't count toward the plan's progress.
 */
export function itemCompletion(mod: PlanModule, ctx: PlanCompletionCtx): ItemCompletion {
  if (!mod.refId) {
    return null;
  }
  switch (mod.kind) {
    case "lesson": {
      return ctx.lessonStatusByRef?.get(mod.refId) === "completed" ? "done" : "pending";
    }
    case "assignment": {
      return ctx.submittedByAssignment?.get(mod.refId) ? "done" : "pending";
    }
    case "video": {
      return ctx.videoDoneByRef?.get(mod.refId) ? "done" : "pending";
    }
    default: {
      // resource | forum | live_session | future kinds → no tick.
      return null;
    }
  }
}

/** Stable comparator: phase (pre→live→post) → position → id. */
export function comparePlanModules(a: PlanModule, b: PlanModule): number {
  const byPhase = phaseRank(a.phase) - phaseRank(b.phase);
  if (byPhase !== 0) {
    return byPhase;
  }
  const byPos = a.position - b.position;
  if (byPos !== 0) {
    return byPos;
  }
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
}

/** Groups the plan by phase (each group ordered by position). Doesn't mutate the input. */
export function groupPlanByPhase(mods: readonly PlanModule[]): Record<PlanPhase, PlanModule[]> {
  const out: Record<PlanPhase, PlanModule[]> = { pre: [], live: [], post: [] };
  for (const m of mods.toSorted(comparePlanModules)) {
    out[m.phase].push(m);
  }
  return out;
}

/**
 * Progress of the plan (or of a phase): `done` = items with tick "done"; `total` =
 * items that score (non-null completion). Informational ones don't count.
 */
export function planProgress(
  mods: readonly PlanModule[],
  ctx: PlanCompletionCtx,
): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const m of mods) {
    const c = itemCompletion(m, ctx);
    if (c === null) {
      continue;
    }
    total += 1;
    if (c === "done") {
      done += 1;
    }
  }
  return { done, total };
}
